import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import Overlay from "../../Overlay";
import useCommission from "../../../hooks/useCommissions";
import TextBox from "../TextBox";
import { Form, Formik } from "formik";

import "../../../styles/submitDraft.scss";

import { ReactComponent as FileUploaderPlaceholderSvg } from "../../../assets/icons/fileuploader-placeholder.svg";
import { ReactComponent as TrashSvg } from "../../../assets/icons/trash.svg";
import Button from "../Button";
import { PAGE_STATE, langComponent } from "../../../constants";
import useChat from "../../../hooks/useChat";
import AppContext from "../../../AppContext";

const SubmitDraft = ({ chatSessionId, commission, onClose, onSuccess }) => {
  const { getLang } = useContext(AppContext);
  const Translation = getLang(langComponent.chat);
  const TranslationCommon = getLang(langComponent.common);
  const [isDisplay, setIsDisplay] = useState(true);
  const {
    patchCommission,
    createSubmission,
    querySubmissions,
    uploadSubmissionFiles,
  } = useCommission();
  const [draftNo, setDraftNo] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [files, setFiles] = useState([]);
  const [loadingState, setLoadingState] = useState(PAGE_STATE.IDLE);
  const { createChat } = useChat();

  useEffect(() => {
    init();
  }, []);

  const init = async () => {
    const { data: submissions } = await querySubmissions(commission._id);

    setDraftNo(submissions.length + 1);
  };

  const handleFiles = (files) => {
    if (files.length === 0) return;

    setFiles((curr) => {
      // * Filter out duplicate files
      const existingFilenames = curr.map((c) => c.name);

      const _files = [...files].filter(
        (file) => !existingFilenames.includes(file.name)
      );

      return [...curr, ..._files];
    });
  };

  const handleRemoveImage = (file) => {
    setFiles((curr) => [...curr.filter((f) => f.name !== file.name)]);
  };

  const handleDragDropEvents = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if ((e.type === "dragenter" || e.type === "dragover") && !dragActive) {
      setDragActive(true);
    }

    if ((e.type === "dragleave" || e.type === "drop") && dragActive) {
      setDragActive(false);
    }

    if (e.type === "drop") {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleSubmit = async ({ link }) => {
    setLoadingState(PAGE_STATE.LOADING);

    try {
      const submission = await createSubmission(commission._id, {
        draft: draftNo,
        link: link || null,
      });

      if (files.length > 0) {
        await uploadSubmissionFiles(commission._id, submission._id, files);
      }

      await patchCommission(commission._id, { status: 3 });

      // * Add chat message
      await createChat(chatSessionId, 6, {
        draft: {
          commission: commission._id,
          submission: submission._id,
        },
      });

      setIsDisplay(false);
      onSuccess();
    } catch (error) {
      setLoadingState(PAGE_STATE.IDLE);
      console.error(error);
    }
  };

  const handleClose = () => {
    setIsDisplay(false);
    onClose();
  };

  if (!isDisplay) {
    return null;
  }

  return (
    <Overlay
      onClose={handleClose}
      content={
        <div className="submit-draft">
          <h2>{Translation("Submit Draft")}</h2>
          <Formik initialValues={{ link: "" }} onSubmit={handleSubmit}>
            <Form className="submit-draft-form">
              <div className="submit-draft-form-row">
                <span>{Translation("Draft no")}</span>
                <span>{draftNo}</span>
              </div>
              <TextBox
                name="link"
                label={Translation("Link (Optional)")}
                placeholder={Translation("Work link if available")}
              />
              <div
                className={`new-project-image-upload-fileuploader ${
                  dragActive
                    ? "new-project-image-upload-fileuploader--highlight"
                    : ""
                }`}
                onDragEnter={handleDragDropEvents}
                onDragOver={handleDragDropEvents}
                onDragLeave={handleDragDropEvents}
                onDrop={handleDragDropEvents}
              >
                <FileUploaderPlaceholderSvg />
                <p className="new-project-image-upload-fileuploader__info">
                  {Translation("Drop your files here or")}{" "}
                  <label
                    className="new-project-image-upload-fileuploader__input-trigger"
                    htmlFor="new-project-image-upload-fileuploader__input"
                  >
                    {Translation("browse")}
                  </label>
                </p>
                <input
                  id="new-project-image-upload-fileuploader__input"
                  type="file"
                  multiple
                  accept="*"
                  onChange={(e) => handleFiles(e.target.files)}
                />
              </div>
              <div className="new-project-image-upload-gallery-scroller">
                <div className="new-project-image-upload-gallery">
                  {[...files].map((file) => {
                    const _src = URL.createObjectURL(file);
                    return (
                      <div
                        key={file._id}
                        className="new-project-image-upload-gallery-image"
                      >
                        <img
                          className="new-project-image-upload-gallery-image__preview"
                          alt="preview image"
                          src={_src}
                        />
                        <TrashSvg
                          className="new-project-image-upload-gallery-image__trash"
                          onClick={() => handleRemoveImage(file)}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
              {loadingState !== PAGE_STATE.LOADING && (
                <Button label={TranslationCommon("Save")} type="submit" />
              )}
            </Form>
          </Formik>
        </div>
      }
    />
  );
};

SubmitDraft.propTypes = {};

export default SubmitDraft;
